// src/context/AuthContext.tsx
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';

const SIGN_IN = gql`
  mutation SignInUser($input: SignInUserInput!) {
    signInUser(input: $input) {
      token
      errors
    }
  }
`;

const SIGN_UP = gql`
  mutation SignUpUser($input: SignUpUserInput!) {
    signUpUser(input: $input) {
      token
      errors
    }
  }
`;

const CURRENT_USER = gql`
  query CurrentUser {
    currentUser {
      id
      email
      name
      createdAt
      about
      reviewCount
      ratingAverage
      avatarImageUrl
      toolCount
      lendToolBookingActionCount
      borrowToolBookingActionCount
      completedLendBookingTotalCents
      unreadMessageCount
    }
  }
`;

const AuthContext = createContext<any>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [signInMutation] = useMutation(SIGN_IN);
  const [signUpMutation] = useMutation(SIGN_UP);
  const [fetchUser] = useLazyQuery(CURRENT_USER, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setUser(data?.currentUser || null);
      setIsAuthenticated(!!data?.currentUser);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  useEffect(() => {
    const token = localStorage.getItem('jwt');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data } = await signInMutation({ variables: { input: { email, password } } });
    if (data?.signInUser?.token) {
      localStorage.setItem('jwt', data.signInUser.token);
      setIsAuthenticated(true);
      const result = await fetchUser();
      if (result?.data?.currentUser) {
        setUser(result.data.currentUser);
      }
      return { success: true };
    }
    return { success: false, errors: data?.signInUser?.errors || ['Unknown error'] };
  };

  const signUp = async (name: string, email: string, password: string) => {
    const { data } = await signUpMutation({ variables: { input: { name, email, password } }});
    if (data?.signUpUser?.token) {
      localStorage.setItem('jwt', data.signUpUser.token);
      setIsAuthenticated(true);
      fetchUser();
      return { success: true };
    }
    return { success: false, errors: data?.signUpUser?.errors || ['Unknown error'] };
  };

  const signOut = () => {
    localStorage.removeItem('jwt');
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
